/**
 * The old Tulka form's (ChatWizard) on-site backup choice, kept as structured
 * fields instead of Notes text (TUL-5956).
 *
 * presenceBackup: '' | 'remote' | 'onsite' ('onsite' means cancel if no on-site interpreter)
 * backupCallType: '' | 'call' | 'conference'
 */

const PREFERENCE_DEFAULTS = {
  excludedInterpreterIds: [],
  acceptsUnqualifiedInterpreter: false,
  acceptsRussianForUkrainian: false,
};

/**
 * The stored on-site fallback of a booking from the API: 'cancel' | 'remote' | null.
 * Shared by both prebooking forms. Admin-set backups, and bookings older than the
 * backend's backfill window, have only the backup columns and no onSiteFallback;
 * a set backupPresenceForOnSite then means remote. An explicit onSiteFallback wins.
 * The internal Notes are never read.
 */
export const getStoredOnSiteFallback = (prebooking = {}) => {
  if (prebooking.presence !== 'onsite') {
    return prebooking.customerPreferences?.onSiteFallback || null;
  }
  return prebooking.customerPreferences?.onSiteFallback
    || (prebooking.backupPresenceForOnSite ? 'remote' : null);
};

// An edit starts from the booking's stored choice, so saving without touching
// it keeps it. A copy still asks for it.
export const getInitialPresenceBackup = (editMode, routeParams = {}) => {
  const onSiteFallback = editMode ? getStoredOnSiteFallback(routeParams) : null;
  if (onSiteFallback === 'remote') {
    return 'remote';
  }
  return onSiteFallback === 'cancel' ? 'onsite' : '';
};

// Only the two connection types this form offers are mapped; any other stored
// combination is left out of the request. An edit saved in place then keeps the
// stored columns, but an edit that recreates the booking (date, duration,
// presence, language, address or level changed) gets the backend's default
// remote backup, external/call.
export const getInitialBackupCallType = (editMode, routeParams = {}) => {
  if (!editMode || getStoredOnSiteFallback(routeParams) !== 'remote') {
    return '';
  }
  if (routeParams.backupCallTypeForOnSite === 'call') {
    return 'call';
  }
  if (routeParams.backupCallTypeForOnSite === 'data' && routeParams.backupPresenceForOnSite === 'external') {
    return 'conference';
  }
  return '';
};

// customerPreferences is replaced as a whole, so the values this form has no
// controls for are kept from the booking being edited or copied.
export const buildOnSiteBackupFields = ({ presence, presenceBackup, backupCallType, storedPreferences }) => {
  const fields = {
    customerPreferences: {
      ...PREFERENCE_DEFAULTS,
      ...storedPreferences,
      onSiteFallback: null,
    },
  };
  if (presence !== 'onsite' || !presenceBackup) {
    return fields;
  }
  if (presenceBackup === 'onsite') {
    fields.customerPreferences.onSiteFallback = 'cancel';
    return fields;
  }
  fields.customerPreferences.onSiteFallback = 'remote';
  if (backupCallType) {
    // Phone and conference/virtual room are both external connections
    fields.backupCallTypeForOnSite = backupCallType === 'conference' ? 'data' : 'call';
    fields.backupPresenceForOnSite = 'external';
  }
  return fields;
};
